/**
 * Wire types for Evals → Assignments.
 *
 * Mirrors `Backend/functions/src/eval/assignmentQuality.ts`. See
 * docs/FeatureDesigns/Evals/assignment-analysis-quality.md.
 */

/** Config key the backend uses for submissions with no resolved pipeline config. */
export const UNATTRIBUTED_CONFIG = '(unattributed)';

/** Renders an unattributed config as an em-dash — never as a fabricated
 *  "default". See operation-pipeline-config-name.md. */
export const configLabel = (config: string): string =>
    (config === UNATTRIBUTED_CONFIG ? '—' : config);

export interface AssignmentKey {
    questionSetId: string;
    assignedById: string;
    batchId?: string;
}

export interface AssignmentSummary extends AssignmentKey {
    title: string;
    modality?: string;
    subject: string;
    totalQuestions: number;
    assignedByName?: string;
    assignedByType?: string;
    dueAt?: string;
    createdAt: string;
    students: number;
    declined: number;
    closed: number;
}

export interface AssignmentListResult {
    assignments: AssignmentSummary[];
    /** Assigner filter options for the window, independent of the current filter. */
    assigners: { id: string; name: string }[];
    from: string;
    to: string;
    scanned: number;
    truncated: boolean;
}

export interface AssignmentCell {
    attempts: number;
    /** DISTINCT students behind those analyses. Differs from `attempts` whenever
     *  anyone retook, which is exactly when the difference matters. Mirrors the
     *  backend's AssignmentCell in functions/src/eval/assignmentQuality.ts. */
    students: number;
    analysed: number;
    failed: number;
    pending: number;
    skipped: number;
    thumbsDown: number;
    avgFinalScore?: number;
    avgCorrectnessScore?: number;
    avgApproachScore?: number;
    /** Analyses in this cell — one per (student, attempt), so a retake counts
     *  again. The unit is the analysis, not the student. */
    judged: number;
    /** Judged BAD overall — the count worth acting on, and worth linking to.
     *  A mean hides it: two good and one bad averages to acceptable. */
    judgedBad: number;
    /** Mean assessment quality, 0–1 on the three-valued scale. */
    qualityOverall?: number;
    qualityAccuracy?: number;
    qualityApproach?: number;
}

export type VerdictValue = 'good' | 'acceptable' | 'bad';

/** How good OUR assessment was — never the student's score. */
export interface AssessmentQuality {
    evaluator: 'human' | 'judge';
    verdict: VerdictValue;
    accuracy?: VerdictValue;
    accuracyReason?: string;
    approach?: VerdictValue;
    approachReason?: string;
    failureTags: string[];
    critique?: string;
}

/** Mirrors the backend's verdictFromScore: stored numeric, read back as a word. */
export function verdictFromScore(score: number): VerdictValue {
    if (score >= 0.75) return 'good';
    return score >= 0.25 ? 'acceptable' : 'bad';
}

export interface PromoteAssignmentResult {
    datasetId: string;
    readableId: string;
    captured: number;
    skipped: number;
}

export interface JudgeAssignmentResult extends PromoteAssignmentResult {
    judge: {
        cellsConsidered: number;
        cellsJudged: number;
        batches: number;
        failedBatches: number;
    };
}

export interface AssignmentMatrixQuestion {
    questionId: string;
    questionText?: string;
    questionType?: string;
    order: number;
    cells: Record<string, AssignmentCell>;
    total: AssignmentCell;
}

export interface AssignmentMatrix {
    assignment: AssignmentSummary;
    configs: string[];
    questions: AssignmentMatrixQuestion[];
    students: {
        studentId: string;
        name: string;
        status: string;
        attempts: number;
        analyses: number;
        questions: number;
        avgCorrectnessScore?: number;
        judged: number;
        qualityOverall?: number;
    }[];
    total: AssignmentCell;
}

/**
 * What the analysis produced and how healthy it looks.
 *
 * `correctnessScore` / `approachScore` are the STUDENT's scores.
 * `assessmentQuality` is how good OUR analysis of them was — populated once the
 * bench judge runs (design §4). Same words, opposite subject; keep the labels apart.
 */
export interface AnalysisQualityFacts {
    config?: string;
    status?: string;
    failureKind?: string;
    failureMessage?: string;
    correctnessScore?: number;
    approachScore?: number;
    finalScore?: number;
    marksAwarded?: number;
    maxMarks?: number;
    qualityChecks: string[];
    thumbsDown: boolean;
    /** Admin reviews of this analysis, newest write per admin. Absent from a
     *  backend older than this field. */
    reviews?: AnalysisReview[];
    llmStudentAnswer?: string;
    correctness?: string;
    llmCorrectAnswer?: string;
    totalCostUsd?: number;
    /** Model + provider behind the PRIMARY step of this analysis. */
    model?: string;
    provider?: string;
    modelInputId?: string;
    primaryOutputId?: string;
    /** Present once this assignment has been judged. */
    assessmentQuality?: AssessmentQuality;
    /** Human per-step verdicts on this analysis. The judge's are absent by
     *  design — those are summarised in `assessmentQuality`; this field is
     *  about what a person looked at. */
    stepVerdicts?: StepVerdictChip[];
    /** Where this analysis lives as a bench row, once captured. Absent means
     *  nothing has judged it per step yet. */
    benchRow?: { datasetId: string; scenarioInputId: string };
}

export interface AnalysisReview {
    rating: 'positive' | 'negative';
    comment?: string;
    by: string;
    at: string;
}

export interface StudentAnswer {
    text?: string;
    imageUrl?: string;
    selectedOptions?: string[];
}

export interface AssignmentQuestionRow extends AnalysisQualityFacts {
    studentId: string;
    studentName: string;
    grantStatus: string;
    questionId: string;
    questionText?: string;
    questionType?: string;
    correctAnswer?: string;
    /** Paper order, so the table sorts by question without a join. */
    questionOrder: number;
    submissionId?: string;
    sessionId?: string;
    /** Which attempt this row is, counting the student's first as 1. */
    attemptNumber?: number;
    attemptCount?: number;
    /** The analysis the attempt currently points at. False ⇒ superseded by a
     *  later submission of the same question, or by a re-evaluation. */
    current?: boolean;
    supersededCount?: number;
    rerunOf?: string;
    submittedAt?: string;
    timeTakenSeconds?: number;
    skipped: boolean;
    answer: StudentAnswer;
}

export interface AssignmentQuestionRef {
    id: string;
    questionText?: string;
    questionType?: string;
    correctAnswer?: string;
    diagramUrl?: string;
    order: number;
}

export interface AssignmentAnalyses {
    assignment: AssignmentSummary;
    questions: AssignmentQuestionRef[];
    rows: AssignmentQuestionRow[];
}

export interface StudentAnalysisRow extends AnalysisQualityFacts {
    submissionId: string;
    submittedAt: string;
    questionId: string;
    questionText?: string;
    questionType?: string;
    correctAnswer?: string;
    subject?: string;
    testSessionId?: string;
    /** The assignment this attempt sat under; absent for practice work. */
    questionSetId?: string;
    questionSetTitle?: string;
    timeTakenSeconds?: number;
    answer: StudentAnswer;
}

export interface StudentAnalysesResult {
    student: { id: string; name: string };
    rows: StudentAnalysisRow[];
    offset: number;
    limit: number;
    hasMore: boolean;
}

/** A verdict on ONE step of one analysis — the per-step detail the list views
 *  collapse to a single word. */
/** One step's human verdict, flattened for a table cell. */
export interface StepVerdictChip {
    role: string;
    stepType?: string;
    verdict: VerdictValue;
    failureTags: string[];
}

export interface AssignmentBenchRowResult {
    datasetId: string;
    scenarioInputId: string;
    /** False when the row was already there — a second click on the same row. */
    captured: boolean;
}

export interface AnalysisStepVerdict {
    outputId: string;
    stepIndex: number;
    stepType?: string;
    role: string;
    pipelineName?: string;
    promptId?: string;
    promptVersion?: number;
    provider?: string;
    model?: string;
    evaluator: 'human' | 'judge';
    verdict: VerdictValue;
    conditionalVerdict?: VerdictValue;
    accuracy?: VerdictValue;
    accuracyReason?: string;
    approach?: VerdictValue;
    approachReason?: string;
    failureTags: string[];
    critique?: string;
    at: string;
    stale: boolean;
}
